import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Building2, Mail, Phone, Briefcase, FileText,
  CheckCircle, Clock, Loader2, AlertTriangle, ArrowRight,
  ChevronDown, ChevronUp, MessageSquare, Calendar, Target,
  ShieldAlert, TrendingDown, Hash, CircleDot, FolderOpen, ShieldCheck, ClipboardCheck, FileCheck, Users
} from 'lucide-react';
import { partnerCasesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { FilingIndexSection, EligibilityEngine, BSAnalysisPanel } from '../admin/AdminCCFSLeads';

const STATUSES = [
  { key: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'financials_in_preparation', label: 'Financials in Prep', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'sent_to_cs', label: 'Sent to CS', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  { key: 'filing_in_progress', label: 'Filing in Progress', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
];

const TASK_STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-500',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
};

const DOC_STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-500',
  uploaded: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
};

const PHASE_COLORS = {
  'Financial Preparation': 'border-l-blue-500 bg-blue-50/30',
  'Statutory Audit': 'border-l-violet-500 bg-violet-50/30',
  'Tax Clearances': 'border-l-amber-500 bg-amber-50/30',
  'Financial Closure': 'border-l-red-500 bg-red-50/30',
  'Annual Filings (CCFS Window)': 'border-l-cyan-500 bg-cyan-50/30',
  'Pending Compliances': 'border-l-orange-500 bg-orange-50/30',
  'Strike-off Preparation (Sec 248)': 'border-l-rose-500 bg-rose-50/30',
  'STK-2 Filing': 'border-l-indigo-500 bg-indigo-50/30',
  'Post-Filing Monitoring': 'border-l-emerald-500 bg-emerald-50/30',
  'General': 'border-l-slate-400 bg-slate-50/30',
};

export default function PartnerCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useOutletContext();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadDetail, setLeadDetail] = useState(null);
  const [showLeadAnalysis, setShowLeadAnalysis] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showScope, setShowScope] = useState(true);
  const [showContext, setShowContext] = useState(true);
  const [collapsedPhases, setCollapsedPhases] = useState({});

  const profession = partner?.profession;

  useEffect(() => {
    partnerCasesAPI.getCase(id)
      .then(res => setCaseData(res.data))
      .catch(() => { toast.error('Failed to load case'); navigate('/r-pace/dashboard'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Fetch full lead detail for read-only analysis sections
  useEffect(() => {
    if (!caseData?.id) return;
    const token = localStorage.getItem('rpace_token');
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/partners/cases/${caseData.id}/lead-detail`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setLeadDetail(data); })
      .catch(() => {});
  }, [caseData?.id]);

  const updateTask = async (taskIdx, newStatus) => {
    try {
      await partnerCasesAPI.updateTask(id, taskIdx, newStatus);
      const res = await partnerCasesAPI.getCase(id);
      setCaseData(res.data);
      toast.success('Task updated');
    } catch { toast.error('Failed to update task'); }
  };

  const updateDoc = async (docIdx, newStatus) => {
    try {
      await partnerCasesAPI.updateDocument(id, docIdx, newStatus);
      const res = await partnerCasesAPI.getCase(id);
      setCaseData(res.data);
      toast.success('Document updated');
    } catch { toast.error('Failed to update document'); }
  };

  const executeAction = async (action) => {
    setActionLoading(true);
    try {
      const res = await partnerCasesAPI.executeAction(id, action);
      toast.success(res.data.message || 'Action completed');
      const caseRes = await partnerCasesAPI.getCase(id);
      setCaseData(caseRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setActionLoading(false); }
  };

  const togglePhase = (phase) => {
    setCollapsedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const scope = (!loading && caseData) ? (profession === 'CA' ? caseData.ca_scope : caseData.cs_scope) : null;
  const tasks = scope?.tasks || [];
  const documents = scope?.documents || [];

  // Group tasks by phase (must be called before any conditional return)
  const tasksByPhase = useMemo(() => {
    const groups = [];
    const seen = new Set();
    let globalIdx = 0;
    for (const task of tasks) {
      const phase = task.phase || 'General';
      if (!seen.has(phase)) {
        seen.add(phase);
        groups.push({ phase, tasks: [] });
      }
      const group = groups.find(g => g.phase === phase);
      group.tasks.push({ ...task, globalIdx });
      globalIdx++;
    }
    return groups;
  }, [tasks]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!caseData) return <div className="p-8 text-center text-slate-500">Case not found</div>;

  const currentStatusIdx = STATUSES.findIndex(s => s.key === caseData.status);
  const leadCtx = caseData.lead_context || {};
  const filingSummary = leadCtx.filing_summary || {};

  const totalCompleted = tasks.filter(t => t.status === 'completed').length;
  const availableAction = getAvailableAction(profession, caseData.status);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="partner-case-detail">
      {/* Back */}
      <button
        onClick={() => navigate('/r-pace/dashboard')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="case-company-name">{caseData.company_name}</h1>
          {leadCtx.lead_number && (
            <span className="text-xs font-bold bg-[#1e3a5f] text-white px-2 py-0.5 rounded" data-testid="case-lead-number">{leadCtx.lead_number}</span>
          )}
          {leadCtx.intent_label && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${leadCtx.intent === 'close' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`} data-testid="case-intent-badge">
              {leadCtx.intent_label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
          {caseData.client_name && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{caseData.client_name}</span>}
          {caseData.client_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{caseData.client_email}</span>}
          {caseData.client_phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{caseData.client_phone}</span>}
        </div>
      </div>

      {/* Status Progress Bar */}
      <Card className="mb-6" data-testid="status-progress">
        <CardContent className="p-5">
          <h3 className="text-xs font-semibold text-slate-600 uppercase mb-3">Case Progress</h3>
          <div className="flex items-center gap-1">
            {STATUSES.map((s, i) => {
              const isActive = i === currentStatusIdx;
              const isDone = i < currentStatusIdx;
              return (
                <div key={s.key} className="flex items-center flex-1">
                  <div className={`flex-1 py-2 px-1 text-[10px] font-medium text-center rounded-md border ${isActive ? s.color + ' ring-2 ring-offset-1 ring-slate-300' : isDone ? 'bg-green-50 text-green-600 border-green-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                    {isDone && <CheckCircle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
                    {s.label}
                  </div>
                  {i < STATUSES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 mx-0.5 shrink-0" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Company Analysis — Read-only from Admin's work */}
      {leadDetail && (
        <Card className="mb-6" data-testid="lead-analysis-card">
          <button onClick={() => setShowLeadAnalysis(!showLeadAnalysis)}
            className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors" data-testid="toggle-lead-analysis">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <FileCheck className="w-4 h-4 text-indigo-600" /> Company Analysis & Compliance
              {leadDetail.documents_folder_url && (
                <a href={leadDetail.documents_folder_url} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-700 ml-1" title="Documents Folder" onClick={e => e.stopPropagation()}>
                  <FolderOpen className="w-4 h-4" />
                </a>
              )}
              {leadDetail.eligibility_engine?.decision && (
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold border ${
                  leadDetail.eligibility_engine.decision === 'direct_closure' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  leadDetail.eligibility_engine.decision === 'not_eligible' ? 'bg-red-50 text-red-600 border-red-200' :
                  'bg-amber-50 text-amber-700 border-amber-200'
                }`}>{leadDetail.eligibility_engine.decision_label || leadDetail.eligibility_engine.decision}</span>
              )}
            </span>
            {showLeadAnalysis ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>

          {showLeadAnalysis && (
            <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
              {/* MCA Master Data */}
              {leadDetail.mca_company_info && (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> MCA Master Data
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(leadDetail.mca_company_info).filter(([k]) => k !== 'company_information').map(([key, val]) => (
                      <div key={key} className="text-xs">
                        <span className="text-slate-400">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</span>{' '}
                        <span className="font-medium text-slate-700">{val || '—'}</span>
                      </div>
                    ))}
                  </div>
                  {/* Directors */}
                  {leadDetail.mca_directors?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400 uppercase font-semibold mb-2">Directors ({leadDetail.mca_directors.length})</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                        {leadDetail.mca_directors.map((d, i) => (
                          <div key={`item-${i}`} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2.5 py-1.5">
                            <span className="font-medium text-slate-700 flex-1">{d.name}</span>
                            <span className="text-slate-400 text-[10px]">DIN: {d.din}</span>
                            {d.din_status && <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${d.din_status === 'active' ? 'bg-emerald-100 text-emerald-700' : d.din_status === 'disqualified' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{d.din_status}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Charges */}
                  {leadDetail.mca_charges?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400 uppercase font-semibold mb-2">Charges ({leadDetail.mca_charges.length})</p>
                      <div className="space-y-1">
                        {leadDetail.mca_charges.map((c, i) => (
                          <div key={`item-${i}`} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2.5 py-1.5">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${c.status === 'Satisfied' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{c.status}</span>
                            <span className="text-slate-700 flex-1 truncate">{c.holder_name}</span>
                            <span className="text-slate-400 text-[10px]">{c.amount || ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filing Index */}
              {leadDetail.filing_index?.total_count > 0 && (
                <FilingIndexSection lead={leadDetail} readOnly />
              )}

              {/* Eligibility Engine */}
              {leadDetail.eligibility_engine && (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold flex items-center gap-1.5 mb-3">
                    <ShieldCheck className="w-3.5 h-3.5" /> R-PACE Eligibility Engine
                    {leadDetail.eligibility_engine?.decision && (
                      <span className={`ml-2 text-[9px] px-2 py-0.5 rounded-full font-bold border ${
                        leadDetail.eligibility_engine.decision === 'direct_closure' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        leadDetail.eligibility_engine.decision === 'not_eligible' ? 'bg-red-50 text-red-600 border-red-200' :
                        'bg-amber-50 text-amber-700 border-amber-200'
                      }`}>{leadDetail.eligibility_engine.decision_label || leadDetail.eligibility_engine.decision}</span>
                    )}
                  </p>
                  <EligibilityEngine lead={leadDetail} readOnly />
                </div>
              )}

              {/* BS & P&L Analysis */}
              {(leadDetail.bs_analysis || leadDetail.aoc4_parsed) && (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold flex items-center gap-1.5 mb-3">
                    <ClipboardCheck className="w-3.5 h-3.5" /> Balance Sheet & P&L Analysis
                  </p>
                  <BSAnalysisPanel lead={leadDetail} readOnly />
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Lead Context — Company & Compliance Summary */}
      {leadCtx.cin && (
        <Card className="mb-6" data-testid="lead-context-card">
          <button
            onClick={() => setShowContext(!showContext)}
            className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
            data-testid="toggle-context"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Target className="w-4 h-4 text-[#1e3a5f]" /> Case Context & Compliance Summary
            </span>
            {showContext ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>

          {showContext && (
            <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
              {/* Key info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold flex items-center gap-1"><Hash className="w-3 h-3" /> CIN</p>
                  <p className="text-sm font-mono font-medium text-slate-800 mt-0.5" data-testid="ctx-cin">{leadCtx.cin}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Company Status</p>
                  <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="ctx-status">
                    {leadCtx.company_status === 'closed' ? 'Business Stopped' : leadCtx.company_status === 'running' ? 'Business Active' : leadCtx.company_status || '—'}
                  </p>
                </div>
                {leadCtx.cessation_fy && (
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Cessation FY</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="ctx-cessation">{leadCtx.cessation_fy}</p>
                  </div>
                )}
                {leadCtx.last_filed_fy && (
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase font-semibold">Last Filed FY</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{leadCtx.last_filed_fy}</p>
                  </div>
                )}
              </div>

              {/* Filing pendency summary */}
              {filingSummary.total_fys > 0 && (
                <div className="bg-amber-50/60 rounded-lg border border-amber-200 p-4" data-testid="ctx-filing-summary">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> Filing Pendency
                  </p>
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-amber-600 font-medium">{filingSummary.fs_pending}</span>
                      <span className="text-amber-500 text-xs ml-1">FYs — FS (AOC-4) pending</span>
                    </div>
                    <div>
                      <span className="text-amber-600 font-medium">{filingSummary.ar_pending}</span>
                      <span className="text-amber-500 text-xs ml-1">FYs — AR (MGT-7) pending</span>
                    </div>
                  </div>
                  {filingSummary.pending_fy_list?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {filingSummary.pending_fy_list.map(fy => (
                        <span key={fy} className="text-[10px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">FY {fy}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Charges + Directors + Savings row */}
              <div className="grid grid-cols-3 gap-3">
                {leadCtx.estimated_savings > 0 && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <p className="text-[10px] text-green-600 uppercase font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Est. Savings</p>
                    <p className="text-lg font-bold text-green-700 mt-0.5">
                      {'\u20B9'}{Math.round(leadCtx.estimated_savings).toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Directors</p>
                  <p className="text-lg font-bold text-slate-800 mt-0.5">{leadCtx.directors_count || '—'}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold flex items-center gap-1">
                    {leadCtx.open_charges > 0 && <ShieldAlert className="w-3 h-3 text-red-500" />} Charges
                  </p>
                  <p className="text-lg font-bold text-slate-800 mt-0.5">
                    {leadCtx.open_charges > 0 ? <span className="text-red-600">{leadCtx.open_charges} open</span> : '0 open'}
                    <span className="text-xs text-slate-400 font-normal ml-1">/ {leadCtx.total_charges || 0} total</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Action Button */}
      {availableAction && (
        <Card className="mb-6 border-l-4 border-l-[#1e3a5f]" data-testid="action-card">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">{availableAction.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{availableAction.description}</p>
            </div>
            <Button
              onClick={() => executeAction(availableAction.action)}
              disabled={actionLoading}
              className="bg-[#1e3a5f] hover:bg-[#152d4a]"
              data-testid="execute-action-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {availableAction.buttonText}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Scope of Work — Phased Checklist */}
      <Card className="mb-6">
        <button
          onClick={() => setShowScope(!showScope)}
          className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
          data-testid="toggle-scope"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            {profession === 'CA' ? <Briefcase className="w-4 h-4 text-[#1e3a5f]" /> : <FileText className="w-4 h-4 text-[#1e3a5f]" />}
            Scope of Work ({profession})
            <span className="text-xs font-normal text-slate-400 ml-1">{totalCompleted}/{tasks.length} tasks done</span>
          </span>
          {showScope ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {showScope && (
          <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
            {/* Overall progress bar */}
            <div className="w-full bg-slate-100 rounded-full h-2" data-testid="scope-progress-bar">
              <div
                className="bg-[#1e3a5f] h-2 rounded-full transition-all duration-500"
                style={{ width: `${tasks.length > 0 ? (totalCompleted / tasks.length) * 100 : 0}%` }}
              />
            </div>

            {/* Tasks grouped by phase */}
            {tasksByPhase.map(({ phase, tasks: phaseTasks }) => {
              const phaseCompleted = phaseTasks.filter(t => t.status === 'completed').length;
              const phaseColor = PHASE_COLORS[phase] || PHASE_COLORS['General'];
              const isCollapsed = collapsedPhases[phase];

              return (
                <div key={phase} className={`rounded-lg border-l-4 ${phaseColor}`} data-testid={`phase-${phase.replace(/\s+/g, '-').toLowerCase()}`}>
                  <button
                    onClick={() => togglePhase(phase)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CircleDot className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{phase}</span>
                      <span className="text-[10px] text-slate-400 font-medium">{phaseCompleted}/{phaseTasks.length}</span>
                    </div>
                    {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-400" />}
                  </button>

                  {!isCollapsed && (
                    <div className="px-4 pb-3 space-y-1.5">
                      {phaseTasks.map((task) => (
                        <div key={task.globalIdx} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`task-${task.globalIdx}`}>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-700'} font-medium`}>{task.name}</p>
                            {task.due_date && (
                              <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" />Due: {task.due_date}</p>
                            )}
                          </div>
                          <select
                            value={task.status}
                            onChange={e => updateTask(task.globalIdx, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${TASK_STATUS_COLORS[task.status]}`}
                            data-testid={`task-status-${task.globalIdx}`}
                          >
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {tasks.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">No tasks assigned yet.</p>
            )}

            {/* Documents */}
            {documents.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Documents ({documents.filter(d => d.status !== 'pending').length}/{documents.length} handled)</p>
                <div className="space-y-1.5">
                  {documents.map((doc, i) => (
                    <div key={`item-${i}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`doc-${i}`}>
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <p className="text-sm text-slate-700 flex-1 font-medium">{doc.name}</p>
                      <select
                        value={doc.status}
                        onChange={e => updateDoc(i, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${DOC_STATUS_COLORS[doc.status]}`}
                        data-testid={`doc-status-${i}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="uploaded">Uploaded</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Detailed Report — read-only preview & download */}
      {leadDetail?.detailed_report && (
        <Card className="mb-6" data-testid="partner-detailed-report-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-slate-700">Detailed Compliance Report</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">v{leadDetail.detailed_report.version || 1}</span>
                {leadDetail.detailed_report.generated_at && (
                  <span className="text-[10px] text-slate-400">Prepared {new Date(leadDetail.detailed_report.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                )}
              </div>
              <Button
                size="sm"
                className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('rpace_token') || localStorage.getItem('ratnatraya_partner_token');
                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                    const res = await fetch(`${apiBase}/api/partners/cases/${id}/detailed-report/pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) throw new Error('download failed');
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Detailed_Report_${leadDetail.company_name || 'Company'}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('Report downloaded');
                  } catch { toast.error('Failed to download report'); }
                }}
                data-testid="partner-download-report-btn"
              >
                <ArrowRight className="w-3 h-3" /> Download PDF
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 italic">The Detailed Compliance Report was shared with the client during the engagement discussion. Download to review the full scope context.</p>
          </CardContent>
        </Card>
      )}

      {/* Shareholding — read-only */}
      {leadDetail?.shareholding && (leadDetail.shareholding.as_on_today?.rows?.length || leadDetail.shareholding.as_on_bs_date?.rows?.length || leadDetail.shareholding.shareholders?.length) > 0 && (
        <Card className="mb-6" data-testid="partner-shareholding-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-700">Shareholding (read-only)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['as_on_today', 'as_on_bs_date'].map(snapKey => {
                const snap = leadDetail.shareholding[snapKey];
                if (!snap || !snap.rows?.length) return null;
                const label = snapKey === 'as_on_today' ? 'As on Today' : `As on BS date${snap.date ? ` (${snap.date})` : ''}`;
                return (
                  <div key={snapKey} className="bg-white border border-slate-200 rounded p-3">
                    <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">{label}{snap.source ? ` · ${snap.source}` : ''}</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[9px] uppercase text-slate-400 border-b border-slate-100">
                          <th className="text-left py-1.5 font-semibold">Shareholder</th>
                          <th className="text-right py-1.5 font-semibold">Shares</th>
                          <th className="text-left py-1.5 font-semibold">Class</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snap.rows.map((r, i) => (
                          <tr key={`${snapKey}-${i}`} className="border-b border-slate-50 last:border-0">
                            <td className="py-1.5 text-slate-700">{r.name || '—'}</td>
                            <td className="py-1.5 text-right text-slate-700">{(r.shares || 0).toLocaleString('en-IN')}</td>
                            <td className="py-1.5 text-slate-500">{r.share_class || 'Equity'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discovery Call — read-only summary + scope checkpoints */}
      {leadDetail?.structured_call_notes && (leadDetail.structured_call_notes.call_date || leadDetail.structured_call_notes.summary) && (
        <Card className="mb-6" data-testid="partner-discovery-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-sky-600" />
              <h3 className="text-sm font-semibold text-slate-700">Discovery Call</h3>
              {leadDetail.structured_call_notes.call_date && (
                <span className="text-[10px] text-slate-400">Held on {leadDetail.structured_call_notes.call_date}</span>
              )}
              {leadDetail.structured_call_notes.caller_name && (
                <span className="text-[10px] text-slate-400">· by {leadDetail.structured_call_notes.caller_name}</span>
              )}
            </div>
            {leadDetail.structured_call_notes.summary && (
              <div className="bg-sky-50 border border-sky-200 rounded p-3 mb-3">
                <p className="text-[10px] font-bold text-sky-700 uppercase tracking-wide mb-1">Key Findings</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{leadDetail.structured_call_notes.summary}</p>
              </div>
            )}
            {leadDetail.structured_call_notes.scope_checkpoints && Object.values(leadDetail.structured_call_notes.scope_checkpoints).some(Boolean) && (
              <div className="bg-violet-50 border border-violet-200 rounded p-3">
                <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wide mb-1.5">Discovery Scope Checkpoints (flagged)</p>
                <ul className="space-y-1">
                  {Object.entries(leadDetail.structured_call_notes.scope_checkpoints).filter(([_, v]) => v).map(([k]) => (
                    <li key={k} className="text-xs text-slate-700 flex items-start gap-1.5">
                      <CheckCircle className="w-3 h-3 text-violet-600 mt-0.5 shrink-0" />
                      <span>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status Stream — shared timeline with admin + counterpart partner */}
      <Card data-testid="case-status-stream">
        <CardContent className="p-5">
          <PartnerStatusStream caseData={caseData} caseId={id} onRefresh={async () => {
            try {
              const res = await partnerCasesAPI.get(id);
              setCaseData(res.data);
            } catch { /* noop */ }
          }} />
        </CardContent>
      </Card>
    </div>
  );
}

function PartnerStatusStream({ caseData, caseId, onRefresh }) {
  const [text, setText] = useState('');
  const [kind, setKind] = useState('note');
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const token = localStorage.getItem('ratnatraya_partner_token') || localStorage.getItem('ratnatraya_token');
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/partners/cases/${caseId}/status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), kind }),
      });
      if (!res.ok) throw new Error('post failed');
      setText('');
      setKind('note');
      onRefresh?.();
      toast.success('Status posted');
    } catch { toast.error('Failed to post status'); }
    finally { setPosting(false); }
  };

  const stream = (caseData.status_stream && caseData.status_stream.length)
    ? [...caseData.status_stream]
    : (caseData.notes || []).map(n => ({ ...n, kind: 'note', by_role: 'admin', by_name: n.by }));

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#1e3a5f]" />
          <h3 className="text-sm font-semibold text-slate-700">Status Stream</h3>
          <span className="text-[10px] text-slate-400">Shared with admin + counterpart partner</span>
        </div>
        <span className="text-[10px] text-slate-400">{stream.length} entries</span>
      </div>
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <select value={kind} onChange={e => setKind(e.target.value)} className="h-9 text-xs px-2 rounded border border-slate-200 bg-white shrink-0" data-testid="stream-kind-select">
          <option value="note">Note</option>
          <option value="status">Status update</option>
          <option value="milestone">Milestone</option>
        </select>
        <input type="text" value={text} onChange={e => setText(e.target.value)} placeholder="What happened on this case?" onKeyDown={e => { if (e.key === 'Enter') post(); }} className="h-9 text-sm flex-1 px-3 rounded border border-slate-200 bg-white" data-testid="stream-input" />
        <Button size="sm" onClick={post} disabled={posting || !text.trim()} className="bg-[#1e3a5f] hover:bg-[#152d4a] h-9" data-testid="add-stream-btn">
          {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Post
        </Button>
      </div>
      {stream.length === 0 ? (
        <p className="text-xs text-slate-400">No updates yet.</p>
      ) : (
        <div className="space-y-2">
          {stream.slice().reverse().map((e, i) => {
            const roleStyle = e.by_role === 'CA' ? 'bg-blue-50 border-blue-200' : e.by_role === 'CS' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200';
            const roleChip = e.by_role === 'CA' ? 'bg-blue-100 text-blue-700' : e.by_role === 'CS' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600';
            const kindChip = e.kind === 'milestone'
              ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">MILESTONE</span>
              : e.kind === 'status'
              ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0">STATUS</span>
              : null;
            return (
              <div key={e.id || i} className={`border rounded-lg p-3 ${roleStyle}`} data-testid={`stream-${i}`}>
                <div className="flex items-start gap-2 mb-1">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${roleChip}`}>{(e.by_role || 'admin').toUpperCase()}</span>
                  {kindChip}
                  <span className="text-[10px] text-slate-500 ml-auto">{new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p className="text-sm text-slate-700">{e.text}</p>
                <p className="text-[10px] text-slate-400 mt-1">{e.by_name || e.by}</p>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function getAvailableAction(profession, status) {
  if (profession === 'CA' && status === 'financials_in_preparation') {
    return {
      action: 'mark_financials_prepared',
      label: 'Financials Ready?',
      description: 'Mark financials as prepared and send for review.',
      buttonText: 'Mark Financials Prepared',
    };
  }
  if (profession === 'CS' && status === 'sent_to_cs') {
    return {
      action: 'start_filing',
      label: 'Ready to File?',
      description: 'Begin the MCA filing process for this case.',
      buttonText: 'Start Filing',
    };
  }
  if (profession === 'CS' && status === 'filing_in_progress') {
    return {
      action: 'mark_filing_complete',
      label: 'Filing Complete?',
      description: 'Mark this case as fully filed and completed.',
      buttonText: 'Mark Filing Complete',
    };
  }
  return null;
}
